import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseService';

interface UnifiedSchedulePageProps {
  serviceType: 'doctor' | 'turf';
}

const UnifiedSchedulePage: React.FC<UnifiedSchedulePageProps> = ({ serviceType }) => {
  const { tenant } = useAuth();
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getServiceConfig = () => {
    switch (serviceType) {
      case 'doctor':
        return {
          settingsPath: '/dashboard/settings',
          title: 'Schedule',
          description: 'View and manage your calendar'
        };
      case 'turf':
        return {
          settingsPath: '/fitness-sports-dashboard/settings',
          title: 'Schedule',
          description: 'View and manage your calendar'
        };
      default:
        return {
          settingsPath: '/dashboard/settings',
          title: 'Schedule',
          description: 'View and manage your calendar'
        };
    }
  };

  const config = getServiceConfig();

  useEffect(() => {
    const fetchCalendarId = async () => {
      if (!tenant?.id) {
        setError('No tenant information available');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tenant_integrations')
          .select('integration_data')
          .eq('tenant_id', tenant.id)
          .eq('integration_type', 'google_calendar')
          .eq('is_connected', 'true')
          .maybeSingle();

        if (error) throw error;

        if (data && data.integration_data && data.integration_data.calendar_id) {
          setCalendarId(data.integration_data.calendar_id);
        } else {
          setError('No Google Calendar integration found. Please connect your Google Calendar in Settings.');
        }
      } catch (err: any) {
        console.error('Error fetching calendar ID:', err.message || err);
        setError('Failed to fetch calendar ID. Please check your Google Calendar integration in Settings.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalendarId();
  }, [tenant?.id]);

  if (isLoading) {
    return (
      <div className="w-full px-0 py-6 sm:px-3 sm:py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600 dark:text-gray-400">
            <svg className="animate-spin h-8 w-8 mx-auto mb-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading calendar...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[62vh] items-center justify-center px-4 py-8">
        <div className="hidden bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">{error}</div>
          </div>
        </div>
        <div className="flex justify-center">
          <button 
            onClick={() => window.location.href = config.settingsPath}
            className="tori-unstyled-button inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[#b9ddff]/18 bg-white/[0.055] px-8 py-4 font-tori-garamond text-2xl font-light normal-case text-blue-100/85 transition hover:bg-[#9ed3ff]/12 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="mr-3 h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            Go to Settings to connect Google Calendar
          </button>
        </div>
      </div>
    );
  }

  const calendarSrc = `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(calendarId || '')}&ctz=Asia%2FKolkata`;
  const openCalendarUrl = `https://calendar.google.com/calendar/u/0/r?cid=${calendarId}`;

  return (
    <div className="w-full px-0 py-6 sm:px-3 sm:py-8">
      <div className="mb-6 flex flex-col items-start text-left md:flex-row md:items-center md:justify-start">
        <div>
          <h1 className="font-tori-garamond text-4xl font-light leading-[0.95] text-white sm:text-5xl lg:text-6xl 2xl:text-7xl">{config.title}</h1>
          <p className="font-tori-garamond mt-2 text-xl font-light leading-tight text-blue-100/45 sm:text-2xl lg:text-3xl">{config.description}</p>
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-[1.35rem] border border-white/10 bg-white/[0.04] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_18px_54px_rgba(0,0,0,0.18)]">
        <div className="responsive-calendar-container" style={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
          <iframe 
            src={calendarSrc}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            frameBorder="0"
            scrolling="no"
            title="Google Calendar"
          ></iframe>
        </div>
      </div>

      <div className="flex justify-end">
        <a 
          href={openCalendarUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
        >
          Open in Google Calendar
          <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default UnifiedSchedulePage;